import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateActiveAlertDraft } from '../../../state/slices/alertCreatorSlice';
import { isValidEditablePolygon } from '../utils/polygonEditor.utils';

const cloneCoordinates = (coordinates) => coordinates.map((coordinate) => ({ ...coordinate }));

const useRescAlertAreaEditor = (coordinates = []) => {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState('reshape');
  const [activeTool, setActiveTool] = useState(null);
  const [pointerLocation, setPointerLocation] = useState(null);
  const [, renderHistory] = useState(0);
  const history = useRef({ original: cloneCoordinates(coordinates), undo: [], redo: [] });
  const savedCoordinates = useMemo(() => coordinates ?? [], [coordinates]);

  const save = useCallback(
    (nextCoordinates) => {
      if (nextCoordinates.length >= 3 && !isValidEditablePolygon(nextCoordinates)) return false;
      history.current.undo.push(cloneCoordinates(savedCoordinates));
      history.current.redo = [];
      dispatch(updateActiveAlertDraft({ coordinates: nextCoordinates }));
      renderHistory((version) => version + 1);
      return true;
    },
    [dispatch, savedCoordinates],
  );

  const undo = useCallback(() => {
    const previous = history.current.undo.pop();
    if (!previous) return;
    history.current.redo.push(cloneCoordinates(savedCoordinates));
    dispatch(updateActiveAlertDraft({ coordinates: previous }));
    renderHistory((version) => version + 1);
  }, [dispatch, savedCoordinates]);

  const redo = useCallback(() => {
    const next = history.current.redo.pop();
    if (!next) return;
    history.current.undo.push(cloneCoordinates(savedCoordinates));
    dispatch(updateActiveAlertDraft({ coordinates: next }));
    renderHistory((version) => version + 1);
  }, [dispatch, savedCoordinates]);

  const cancel = useCallback(() => {
    dispatch(updateActiveAlertDraft({ coordinates: cloneCoordinates(history.current.original) }));
    history.current.undo = [];
    history.current.redo = [];
    renderHistory((version) => version + 1);
  }, [dispatch]);

  return {
    editMode,
    setEditMode,
    activeTool,
    pointerLocation,
    startTool: (tool) => {
      setActiveTool(tool);
      setPointerLocation(null);
    },
    finishTool: () => {
      setActiveTool(null);
      setPointerLocation(null);
    },
    setPointerLocation,
    save,
    undo,
    redo,
    cancel,
    canUndo: history.current.undo.length > 0,
    canRedo: history.current.redo.length > 0,
  };
};

export default useRescAlertAreaEditor;
